import { useContext, useState } from "react";
import { Card, FileText } from "../../../../ui";
import { Context } from "../../../../../context/Context";
import { getStoreOnboardingFileUrlAPI } from "../../../../../utils/api/storeOnboarding";
import { getText } from "../../../onboarding";
import { notifyError } from "../../../../../utils/notify";

const normalizeValue = (value, t) => {
  if (value === true) return t.yes || "Yes";
  if (value === false) return t.no || "No";
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.join("، ") : "-";
  return value;
};

const getFileExtension = (file) => {
  const originalName = String(file?.name || "").trim();
  const nameParts = originalName.split(".");

  if (nameParts.length > 1) {
    return nameParts.pop().toLowerCase();
  }

  const mimeExtensions = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "application/pdf": "pdf",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "xlsx",
    "application/zip": "zip",
    "application/x-zip-compressed": "zip",
  };

  return mimeExtensions[file?.mimeType] || "";
};

const getAdminFileName = ({ file, kind, type }) => {
  if (!file) return "";

  const extension = getFileExtension(file);

  const menuFileNames = {
    restaurantMenuFile: "restaurant-menu",
    foodCostSheetFile: "food-cost-sheet",
    deliveryAppsMenuFile: "delivery-apps-menu",
    itemImagesZipFile: "item-images",
    logoFile: "logo",
  };

  const documentFileNames = {
    cr: "commercial-registration",
    vat: "vat-certificate",
    menu: "menu",
    products: "products",
    logo: "logo",
    iban: "iban-certificate",
    license: "business-license",
    national_address: "national-address",
    other: "other-document",
    restaurant_menu: "restaurant-menu",
    delivery_apps_menu: "delivery-apps-menu",
    item_images_zip: "item-images",
    food_cost_sheet: "food-cost-sheet",
    payment_receipt: "payment-receipt",
  };

  let baseName = "";

  if (kind === "menu") {
    baseName = menuFileNames[type] || "";
  } else if (kind === "document") {
    baseName = documentFileNames[file?.category] || "";
  } else if (kind === "quotation") {
    baseName = "payment-receipt";
  }

  if (!baseName) {
    return file?.name || "";
  }

  return extension ? `${baseName}.${extension}` : baseName;
};

export const Field = ({ label, value }) => {
  const { lang } = useContext(Context);
  const t = getText(lang);

  return (
    <div className="admin-onboarding-field">
      <div className="admin-onboarding-field-label">{label}</div>

      <div className="admin-onboarding-field-value">
        {normalizeValue(value, t)}
      </div>
    </div>
  );
};

export const FileItem = ({
  label,
  file,
  onboardingId,
  kind,
  type,
  index,
}) => {
  const { lang } = useContext(Context);
  const t = getText(lang);
  const [opening, setOpening] = useState(false);

  const displayFileName = file
    ? getAdminFileName({
        file,
        kind,
        type,
      })
    : t.notSelected;

  const openFile = async () => {
    if (!file || opening) return;

    try {
      setOpening(true);

      const token = localStorage.getItem("auth-token");

      if (!token || !onboardingId || !kind) {
        throw new Error("Missing file access data");
      }

      const params = { kind };

      if (type !== undefined) {
        params.type = type;
      }

      if (index !== undefined) {
        params.index = index;
      }

      const fileResponse = await getStoreOnboardingFileUrlAPI(
        token,
        onboardingId,
        params,
      );

      const signedUrl = fileResponse?.url || fileResponse;
      const originalName =
        fileResponse?.originalName || file?.name || displayFileName || "file";
      const fileWithOriginalName = {
        ...file,
        name: originalName,
      };
      const extension = getFileExtension(fileWithOriginalName);
      const downloadFileName =
        getAdminFileName({
          file: fileWithOriginalName,
          kind,
          type,
        }) ||
        originalName ||
        "file";
      const previewableExtensions = [
        "pdf",
        "png",
        "jpg",
        "jpeg",
        "webp",
        "gif",
        "svg",
      ];

      if (previewableExtensions.includes(extension)) {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const downloadResponse = await fetch(signedUrl);

      if (!downloadResponse.ok) {
        throw new Error(
          `Failed to download file: ${downloadResponse.status}`,
        );
      }

      const blob = await downloadResponse.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(error);

      notifyError(
        { ar: "تعذر فتح الملف، يرجى المحاولة مرة أخرى", en: "Unable to open file, please try again" },
        { key: "onboarding-file-open-failed" },
      );
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="admin-onboarding-file-card">
      <div className="admin-onboarding-file-icon">
        <FileText size={22} />
      </div>

      <div className="admin-onboarding-file-info">
        <span className="admin-onboarding-file-badge">
          {label || file?.category || t.file}
        </span>

        <strong title={displayFileName}>{displayFileName}</strong>
      </div>

      {file?.publicId || file?.url ? (
        <button
          type="button"
          className="admin-onboarding-file-open"
          onClick={openFile}
          disabled={opening}
        >
          {opening ? t.loading : t.openFile}
        </button>
      ) : (
        <span className="admin-onboarding-file-missing">-</span>
      )}
    </div>
  );
};

export const StageCard = ({
  title,
  description,
  active,
  onClick,
}) => (
  <button
    type="button"
    className={`admin-onboarding-stage-tab ${active ? "active" : ""}`}
    onClick={onClick}
  >
    <span>{title}</span>
    <small>{description}</small>
  </button>
);

export const DetailSection = ({ title, children }) => (
  <Card
    variant="soft"
    padding="md"
    className="admin-onboarding-detail-section"
  >
    <div className="admin-onboarding-detail-title">{title}</div>
    {children}
  </Card>
);

export const FieldsGrid = ({ children }) => (
  <div className="admin-onboarding-fields-grid">{children}</div>
);
